/**
 * LUBA split feature gallery — Swiper (desktop-only; narrow viewports hide section in CSS).
 * Tabs sync with slide; optional autoplay + progress on active tab.
 */
class SplitFeatureGallery extends HTMLElement {
  constructor() {
    super();
    this._handleDeskNavClick = this._handleDeskNavClick.bind(this);
    this.deskSwiper = null;
    this.deskEl = null;
    this.navBtns = [];
    this._deskNavBound = false;
  }

  connectedCallback() {
    this.deskEl = this.querySelector("[data-sfg-desk-swiper]");
    this.navBtns = Array.from(this.querySelectorAll("[data-sfg-nav]"));

    this.count =
      parseInt(this.dataset.blockCount || "0", 10) ||
      parseInt(
        this.deskEl?.querySelectorAll(".swiper-slide").length || "0",
        10
      ) ||
      0;
    this.autoplayMs = parseInt(this.dataset.autoplayMs || "0", 10);

    if (!this._deskNavBound && this.navBtns.length) {
      this.navBtns.forEach((btn) =>
        btn.addEventListener("click", this._handleDeskNavClick)
      );
      this._deskNavBound = true;
    }

    window.loadSwiper()
      .then(() => {
        if (!this.isConnected) return;
        this._teardownSwiper();
        this._initDesktop();
      })
      .catch((error) => console.error('[split-feature-gallery] Unable to load Swiper.', error));
  }

  disconnectedCallback() {
    if (this._deskNavBound && this.navBtns.length) {
      this.navBtns.forEach((btn) =>
        btn.removeEventListener("click", this._handleDeskNavClick)
      );
      this._deskNavBound = false;
    }
    this._teardownSwiper();
  }

  _handleDeskNavClick(ev) {
    const i = parseInt(ev.currentTarget.getAttribute("data-sfg-nav") || "0", 10);
    if (this.deskSwiper) {
      this.deskSwiper.slideTo(i);
      if (this.autoplayMs > 0 && this.deskSwiper.autoplay) {
        this.deskSwiper.autoplay.stop();
        this.deskSwiper.autoplay.start();
      }
    }
  }

  _teardownSwiper() {
    if (this.deskSwiper) {
      try {
        this.deskSwiper.destroy(true, true);
      } catch (e) {}
      this.deskSwiper = null;
    }
  }

  _reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  _initDesktop() {
    if (!this.deskEl || this.count < 1) return;

    const reduced = this._reducedMotion();
    const opt = {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: reduced ? 0 : 450,
      allowTouchMove: false,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      autoHeight: false,
    };

    if (this.autoplayMs > 0 && !reduced && this.count > 1) {
      opt.autoplay = {
        delay: this.autoplayMs,
        disableOnInteraction: false,
      };
    }

    opt.on = {
      slideChange: (sw) => {
        this._setDesktopNav(sw.activeIndex);
      },
    };

    this.deskSwiper = new window.Swiper(this.deskEl, opt);
    this._setDesktopNav(this.deskSwiper.activeIndex);
    requestAnimationFrame(() => {
      if (this.deskSwiper) {
        try {
          this.deskSwiper.update();
          this.deskSwiper.updateSize();
        } catch (e) {}
      }
    });
  }

  _setDesktopNav(index) {
    this.navBtns.forEach((b, i) => {
      b.setAttribute("aria-selected", i === index ? "true" : "false");
    });

    const useAnim = this.autoplayMs > 0 && !this._reducedMotion();

    this.navBtns.forEach((b, i) => {
      const fill = b.querySelector(".sfg__nav-rule-fill");
      if (!fill) return;
      fill.classList.remove("sfg__nav-rule-fill--animate");
      fill.style.width = "";
      void fill.offsetWidth;
      if (i === index) {
        if (useAnim) {
          fill.classList.add("sfg__nav-rule-fill--animate");
        } else {
          fill.style.width = "72%";
        }
      }
    });
  }
}

if (!customElements.get("split-feature-gallery")) {
  customElements.define("split-feature-gallery", SplitFeatureGallery);
}
